import { useMutation, useQuery, useQueryClient } from "@tanstack/solid-query";
import type { Accessor } from "solid-js";
import { axiosInstance, setAuthToken, setRefreshToken } from "@/api/client";
import {
  AUTH_KEY,
  FREELANCER_INVITATION_LINK_KEY,
  FREELANCER_INVITATIONS_KEY,
  FREELANCER_RECOMMENDATIONS_KEY,
  PROFILE_KEY,
} from "@/api/query-keys";
import type { ProfileV2 } from "@/domains/freelance/profile/api/types";

export interface CreateEmailInvitationRequest {
  email: string;
}

export interface CreateEmailInvitationResponse {
  invitation: {
    id: string;
    invited_by_profile_id: string;
    email: string | null;
    invitation_token: string;
    invitation_type: "email" | "link";
    status: "pending" | "accepted" | "expired" | "cancelled";
    expires_at: string;
    created_at: string;
  };
}

export interface GetInvitationLinkResponse {
  link: string;
  username: string;
}

export interface GetMyInvitationsResponse {
  invitations: Array<{
    id: string;
    invited_by_profile_id: string;
    email: string | null;
    invitation_token: string;
    invitation_type: "email" | "link";
    status: "pending" | "accepted" | "expired" | "cancelled";
    expires_at: string;
    accepted_at: string | null;
    accepted_by_profile_id: string | null;
    created_at: string;
    invitedBy?: {
      id: string;
      full_name: string | null;
      username: string | null;
    };
    acceptedBy?: {
      id: string;
      full_name: string | null;
      username: string | null;
    };
  }>;
}

export interface AcceptInvitationRequest {
  token: string;
  email?: string;
  linkedin_url?: string;
  full_name?: string;
}

export interface AcceptInvitationResponse {
  invitation: {
    id: string;
    status: string;
    invited_by_profile_id?: string;
    invitedBy?: {
      id: string;
      full_name: string | null;
      username: string | null;
    };
  };
  profile: {
    id: string;
    email: string;
    full_name: string | null;
    onboarding_status: ProfileV2["onboarding_status"];
    profile_type?: ProfileV2["profile_type"];
    payment_profile?: ProfileV2["payment_profile"] | null;
    assets?: unknown;
    weekly_hours?: string | null;
    available_from?: string | null;
  };
  isNewUser: boolean;
  tokens: {
    accessToken: string;
    refreshToken: string;
  } | null;
}

export interface CancelInvitationResponse {
  success: boolean;
}

// Base API functions
export const createEmailInvitation = async (
  input: CreateEmailInvitationRequest,
): Promise<CreateEmailInvitationResponse> => {
  const { data } = await axiosInstance.post<CreateEmailInvitationResponse>(
    "/freelancer/invitations/email",
    input,
  );
  return data;
};

export const getInvitationLink =
  async (): Promise<GetInvitationLinkResponse> => {
    const { data } = await axiosInstance.get<GetInvitationLinkResponse>(
      "/freelancer/invitations/link",
    );
    return data;
  };

export const getMyInvitations = async (
  status?: string,
): Promise<GetMyInvitationsResponse> => {
  const params = status ? { status } : {};
  const { data } = await axiosInstance.get<GetMyInvitationsResponse>(
    "/freelancer/invitations",
    {
      params,
    },
  );
  return data;
};

export const acceptInvitation = async (
  input: AcceptInvitationRequest,
): Promise<AcceptInvitationResponse> => {
  const { data } = await axiosInstance.post<AcceptInvitationResponse>(
    "/freelancer/invitations/accept",
    input,
  );
  return data;
};

export const cancelInvitation = async (
  invitationId: string,
): Promise<CancelInvitationResponse> => {
  const { data } = await axiosInstance.delete<CancelInvitationResponse>(
    `/freelancer/invitations/${invitationId}`,
  );
  return data;
};

/**
 * Una raccomandazione ricevuta.
 *
 * Il backend ne serve **tre forme** a seconda di chi chiede
 * (`GET /freelancer/recommendations/:profileId`):
 * - il **proprietario** riceve tutto: ricevute, date, inviti pendenti;
 * - un **loggato non proprietario** riceve le sole ricevute, ma per intero,
 *   con `profile_id` e `avatar_url` del raccomandante (AC5b, emendata il
 *   2026-09-10: foto e link danno credibilità alla raccomandazione a chi
 *   valuta un candidato);
 * - l'**anonimo** riceve quattro campi soltanto: `id`, `full_name`,
 *   `job_title`, `status`.
 *
 * Per questo `profile_id`, `username`, `avatar_url` e `onboarding_status` sono
 * **opzionali**: sulla vista anonima non arrivano affatto. Erano obbligatori,
 * e il compilatore lasciava quindi costruire `href={/profile/${profile_id}}`
 * su un valore che a runtime è `undefined` — cioè `/profile/undefined`. Chi ne
 * ha bisogno adesso deve restringere esplicitamente, e una riga senza
 * `profile_id` ricade sul chip a nome e ruolo sotto qualunque sessione.
 */
export type ProfileRecommendation = Pick<
  ProfileV2,
  "id" | "full_name" | "job_title"
> &
  Partial<Pick<ProfileV2, "username" | "avatar_url" | "onboarding_status">> & {
    profile_id?: string;
    status: "accepted";
  };

/** Una raccomandazione con l'id del raccomandante: solo la vista proprietario. */
export type IdentifiedProfileRecommendation = ProfileRecommendation & {
  profile_id: string;
};

export interface PendingRecommendationGiven {
  id: string;
  email: string;
  status: "pending";
  created_at: string;
  expires_at: string;
}

export interface ProfileRecommendations {
  recommendationsReceived: ProfileRecommendation[];
  /**
   * Solo la vista proprietario. Le altre due non mandano il campo: dichiararlo
   * obbligatorio significava promettere un array che a runtime è `undefined`.
   */
  recommendationsGiven?: ProfileRecommendation[];
  pendingGiven?: PendingRecommendationGiven[];
  invitedByProfile?: Omit<
    ProfileRecommendation,
    "profile_id" | "status"
  > | null;
}

export const getProfileRecommendations = async (
  profileId: string,
): Promise<ProfileRecommendations> => {
  const { data } = await axiosInstance.get<ProfileRecommendations>(
    `/freelancer/recommendations/${profileId}`,
  );
  return data;
};

export const createRecommendation = async (
  profileId: string,
): Promise<{ recommendation: { id: string } }> => {
  const { data } = await axiosInstance.post<{ recommendation: { id: string } }>(
    `/freelancer/recommendations/${profileId}`,
  );
  return data;
};

export const checkHasRecommended = async (
  profileId: string,
): Promise<{ hasRecommended: boolean }> => {
  const { data } = await axiosInstance.get<{ hasRecommended: boolean }>(
    `/freelancer/recommendations/check/${profileId}`,
  );
  return data;
};

export const deleteRecommendation = async (
  recommendationId: string,
): Promise<void> => {
  await axiosInstance.delete(`/freelancer/recommendations/${recommendationId}`);
};

// Custom hooks
export const useCreateEmailInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: (input: CreateEmailInvitationRequest) =>
      createEmailInvitation(input),
    onSuccess: () => {
      // Invalida la query degli inviti per aggiornare la lista
      queryClient.invalidateQueries({ queryKey: [FREELANCER_INVITATIONS_KEY] });
      // Invalida le raccomandazioni così la RecommendationsSection si aggiorna
      queryClient.invalidateQueries({
        queryKey: [FREELANCER_RECOMMENDATIONS_KEY],
      });
    },
  }));
};

export const useGetInvitationLink = () => {
  return useQuery(() => ({
    queryKey: [FREELANCER_INVITATION_LINK_KEY],
    queryFn: () => getInvitationLink(),
    staleTime: 5 * 60 * 1000, // 5 minuti - il link non cambia spesso
    retry: 3, // Riprova 3 volte in caso di errore
    retryDelay: 1000, // Attendi 1 secondo tra i retry
  }));
};

export const useGetMyInvitations = (status?: string) => {
  return useQuery(() => ({
    queryKey: [FREELANCER_INVITATIONS_KEY, status],
    queryFn: () => getMyInvitations(status),
  }));
};

export const useAcceptInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: (input: AcceptInvitationRequest) => acceptInvitation(input),
    onSuccess: async (data) => {
      if (data.tokens) {
        await Promise.all([
          setAuthToken(data.tokens.accessToken),
          setRefreshToken(data.tokens.refreshToken),
          queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] }),
          queryClient.invalidateQueries({ queryKey: [AUTH_KEY] }),
        ]);
      }
      // Invalida le query rilevanti dopo l'accettazione
      queryClient.invalidateQueries({ queryKey: [FREELANCER_INVITATIONS_KEY] });
    },
  }));
};

export const useCancelInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: (invitationId: string) => cancelInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FREELANCER_INVITATIONS_KEY] });
      queryClient.invalidateQueries({
        queryKey: [FREELANCER_RECOMMENDATIONS_KEY],
      });
    },
  }));
};

/**
 * Accessor, per lo stesso motivo delle query di esperienze e formazione: la
 * sheet della ricerca attiva cambia freelancer senza rimontare, e una stringa
 * letta una volta lascerebbe le raccomandazioni del primo sotto il nome del
 * secondo. Una stringa semplice continua a funzionare.
 */
export const useGetProfileRecommendations = (
  profileId: string | Accessor<string>,
) => {
  const id = () => (typeof profileId === "function" ? profileId() : profileId);
  return useQuery(() => ({
    queryKey: [FREELANCER_RECOMMENDATIONS_KEY, id()],
    queryFn: () => getProfileRecommendations(id()),
    enabled: !!id(),
  }));
};

export const useCreateRecommendation = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: (profileId: string) => createRecommendation(profileId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FREELANCER_RECOMMENDATIONS_KEY],
      });
    },
  }));
};

export const useCheckHasRecommended = (profileId: string, enabled: boolean) => {
  return useQuery(() => ({
    queryKey: [FREELANCER_RECOMMENDATIONS_KEY, "check", profileId],
    queryFn: () => checkHasRecommended(profileId),
    enabled: !!profileId && enabled,
  }));
};

export const useDeleteRecommendation = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: (recommendationId: string) =>
      deleteRecommendation(recommendationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FREELANCER_RECOMMENDATIONS_KEY],
      });
    },
  }));
};
